import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import OrdersTable from '@/components/tables/OrdersTable';
// import ShipmentItemsTable from '@/components/tables/ShipmentItemsTable';
import ShipmentsTable from '@/components/tables/ShipmentsTable';
import { useStoreStore } from '@/stores/store-store';
import { useOperationFilterStore } from '@/stores/operation-filter-store';
import OrderDetailsModal from '@/pages/orders/components/OrderDetailsModal';

export default function OperationPage() {
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const subStatusParam = searchParams.get('subStatus');
  const { currentStore } = useStoreStore();

  const normalizeOperationSubStatus = (status: string, subStatus: string | null): string | null => {
    if (status === 'destination_warehouse' && subStatus === 'missing') {
      return 'unconfirmed';
    }

    return subStatus;
  };
  
  // Map tab parameter to status filter
  const getStatusFromTab = (tab: string | null): string => {
    switch (tab) {
      case 'awaiting':
        return 'awaiting';
      case 'in_facility':
        return 'in_facility';
      case 'destination_warehouse':
        return 'destination_warehouse';
      case 'distribution_center':
        return 'distribution_center';
      case 'delivered':
        return 'delivered';
      default:
        return 'awaiting'; // Default to awaiting
    }
  };

  // Map operation status and sub-status to GraphQL status
  const getGraphQLStatusFromSubStatus = (status: string, subStatus: string): string => {
    if (status === 'awaiting') {
      switch (subStatus) {
        case 'on_its_way':
          return 'on-its-way';
        case 'delayed':
          return 'delayed';
        case 'undelivered':
          return 'unreceived';
        default:
          return 'on-its-way';
      }
         } else if (status === 'in_facility') {
       switch (subStatus) {
         case 'in_bound':
           return 'procured-but-not-delivered-to-facility';
         case 'in_facility':
           return 'delivered-to-facility';
         case 'outbound':
           return 'shipped-to-destination-warehouse';
         default:
           return 'procured-but-not-delivered-to-facility';
       }
    } else if (status === 'destination_warehouse') {
      switch (subStatus) {
        case 'inbound':
          return 'shipped-to-destination-warehouse';
        case 'in_warehouse':
          return 'received-at-main-warehouse'; 
        case 'unconfirmed':
          return 'received-at-main-warehouse';
        case 'outbound':
          return 'shipped-from-destination-warehouse';
        case 'missing':
          return 'missing-from-destination-warehouse';
        default:
          return 'shipped-to-destination-warehouse';
      }
    } else if (status === 'distribution_center') {
      switch (subStatus) {
        case 'inbound':
          return 'shipped-to-distribution-center';
        case 'in_center':
          return 'received-at-distribution-center';
        case 'return':
          return 'received-at-distribution-center';
        case 'missing':
          return 'received-at-distribution-center';
        default:
          return 'shipped-to-distribution-center';
      }
    } else if (status === 'delivered') {
      return 'delivered-to-the-customer';
    }
    
    // For other statuses, return the status as is
    return status;
  };

  // Get current status and sub-status from URL params
  const selectedStatus = getStatusFromTab(tabParam);
  const normalizedSubStatusParam = normalizeOperationSubStatus(selectedStatus, subStatusParam);

  const selectedSubStatus = normalizedSubStatusParam || (
    selectedStatus === 'in_facility' ? 'in_bound' : 
    selectedStatus === 'destination_warehouse' ? 'inbound' : 
    selectedStatus === 'distribution_center' ? 'inbound' :
    'on_its_way'
  );
  
  // console.log('URL Parameters Debug:', {
  //   tabParam,
  //   subStatusParam,
  //   selectedStatus,
  //   selectedSubStatus
  // });
  const [showSplitTable, setShowSplitTable] = useState(false);
  const [splitTableType, setSplitTableType] = useState<'take-in' | 'ship' | 'to-center' | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedDetailsOrderCode, setSelectedDetailsOrderCode] = useState<string | undefined>(undefined);

  const handleViewAllClick = (order: any) => {
    const code =
      order.code ||
      order.reference ||
      order.order?.reference ||
      undefined;
    setSelectedDetailsOrderCode(code);
    setIsDetailsOpen(true);
  };
  
  // Use operation filter store
  const {
    searchQuery,
    filters,
    // marketplaceFilter
  } = useOperationFilterStore();

  // Handle Take-in button click
  const handleTakeInClick = () => {
    setShowSplitTable(true);
    setSplitTableType('take-in');
  };

  // Handle Ship button click
  const handleShipClick = () => {
    setShowSplitTable(true);
    setSplitTableType('ship');
  };

  // Handle back to normal table
  const handleBackToNormalTable = () => {
    setShowSplitTable(false);
    setSplitTableType(null);
    
    // Dispatch refresh event to update the main table data
    const refreshEvent = new CustomEvent('refresh-operation-data');
    window.dispatchEvent(refreshEvent);
  };

  // Handle shipment submit button click
  const handleShipmentSubmitClick = () => {
    // Dispatch a custom event that the SplitShipmentItemsTable can listen to
    const event = new CustomEvent('shipment-submit-clicked', {
      detail: { action: 'submit' }
    });
    window.dispatchEvent(event);
  };

  // Handle To Center submission (generate batch → assign to center)
  useEffect(() => {
    const handler = () => {
      window.dispatchEvent(new CustomEvent('to-center-submit')); // SplitOrdersTable listens and will emit right-table orders
    };
    window.addEventListener('to-center-submit-clicked', handler);
    return () => window.removeEventListener('to-center-submit-clicked', handler);
  }, []);

  // Handle To Center (Destination Warehouse → In-Warehouse)
  useEffect(() => {
    const handler = () => {
      if (selectedStatus === 'destination_warehouse' && selectedSubStatus === 'in_warehouse') {
        setShowSplitTable(true);
        setSplitTableType('to-center');
        // Scroll to the orders table area for visibility
        setTimeout(() => {
          const container = document.querySelector('[data-component="OrdersTableContainer"]');
          if (container) {
            (container as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 0);
      }
    };
    window.addEventListener('to-center-clicked', handler);
    return () => window.removeEventListener('to-center-clicked', handler);
  }, [selectedStatus, selectedSubStatus]);

  // Remove the problematic useEffect that was resetting split table on sub-status change
  // This was preventing the Ship button from working

  // Debug logging
  const graphQLStatus = getGraphQLStatusFromSubStatus(selectedStatus, selectedSubStatus);
  console.log('Operation Page Debug:', {
    selectedStatus,
    selectedSubStatus,
    graphQLStatus,
    tabParam,
    subStatusParam: searchParams.get('subStatus'),
    showSplitTable,
    splitTableType,
    currentStoreId: currentStore?.id
  });

  // Check if we should show split table (In Facility -> In Bound or In Facility -> In Facility)
  const shouldShowSplitTable = showSplitTable && (
    (selectedStatus === 'in_facility' && (selectedSubStatus === 'in_bound' || selectedSubStatus === 'in_facility')) ||
    (selectedStatus === 'destination_warehouse' && selectedSubStatus === 'in_warehouse')
  );

  // Pass the Take-in and Ship handlers to the LayoutWrapper through custom events
  useEffect(() => {
    const handleTakeInFromHeader = (event: CustomEvent) => {
      // Use the state from the event detail (from LayoutWrapper)
      const eventSelectedStatus = event.detail?.selectedStatus;
      const eventSelectedSubStatus = event.detail?.selectedSubStatus;
      
      if (eventSelectedStatus === 'in_facility' && eventSelectedSubStatus === 'in_bound') {
        if (event.detail?.isActive) {
          handleTakeInClick();
        } else {
          handleBackToNormalTable();
        }
      }
    };

    const handleShipFromHeader = (event: CustomEvent) => {
      // Use the state from the event detail (from LayoutWrapper)
      const eventSelectedStatus = event.detail?.selectedStatus;
      const eventSelectedSubStatus = event.detail?.selectedSubStatus;
      
      if (eventSelectedStatus === 'in_facility' && eventSelectedSubStatus === 'in_facility') {
        if (event.detail?.isActive) {
          handleShipClick();
        } else {
          handleBackToNormalTable();
        }
      }
    };

    // Add event listeners for Take-in and Ship button clicks from header
    window.addEventListener('take-in-clicked', handleTakeInFromHeader as EventListener);
    window.addEventListener('ship-clicked', handleShipFromHeader as EventListener);
    
    return () => {
      window.removeEventListener('take-in-clicked', handleTakeInFromHeader as EventListener);
      window.removeEventListener('ship-clicked', handleShipFromHeader as EventListener);
    };
  }, []); // Remove dependencies to avoid recreation

  // Listen for sub-status clicks to reset split table when changing subtabs
  useEffect(() => {
    const handleSubStatusClick = (event: CustomEvent) => {
      const { selectedStatus: clickedStatus } = event.detail;
      // Always reset split table when switching sub-status within operation tabs
      if (clickedStatus === selectedStatus) {
        setShowSplitTable(false);
        setSplitTableType(null);
      }
    };

    // Add event listener for sub-status clicks
    window.addEventListener('sub-status-clicked', handleSubStatusClick as EventListener);
    
    return () => {
      window.removeEventListener('sub-status-clicked', handleSubStatusClick as EventListener);
    };
  }, []);

  // Destination warehouse local flows use REST warehouse endpoints to mirror legacy workflow
  const isDestinationWarehouseRestSubStatus =
    selectedStatus === 'destination_warehouse' &&
    ['inbound', 'in_warehouse', 'outbound', 'unconfirmed'].includes(selectedSubStatus);

  const isDistributionCenterRestSubStatus =
    selectedStatus === 'distribution_center' &&
    ['inbound', 'in_center', 'return', 'missing'].includes(selectedSubStatus);

  if (isDestinationWarehouseRestSubStatus || isDistributionCenterRestSubStatus) {
    return (
      <div className="space-y-6">
        <OrdersTable 
          statusFilter={graphQLStatus} 
          subStatusFilter={selectedSubStatus}
          originalStatus={selectedStatus}
          useGraphQL={false}
          storeId={currentStore?.id}
          showSplitTable={shouldShowSplitTable}
          splitTableType={splitTableType}
          onTakeInClick={handleTakeInClick}
          onBackToNormalTable={handleBackToNormalTable}
          onShipmentSubmitClick={handleShipmentSubmitClick}
          searchQuery={searchQuery}
          filters={filters}
          onViewAllClick={handleViewAllClick}
        />

        <OrderDetailsModal
          isOpen={isDetailsOpen}
          onClose={() => setIsDetailsOpen(false)}
          orderCode={selectedDetailsOrderCode}
        />
      </div>
    );
  }

  // If In Facility -> Outbound, show shipments data using ShipmentsTable (original design)
  if (selectedStatus === 'in_facility' && selectedSubStatus === 'outbound') {
    return (
      <div className="space-y-6">
        <ShipmentsTable storeId={currentStore?.id} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Orders Table */}
             <OrdersTable 
        statusFilter={graphQLStatus} 
        subStatusFilter={selectedSubStatus}
        originalStatus={selectedStatus}
        useGraphQL={true}
        storeId={currentStore?.id}
        showSplitTable={shouldShowSplitTable}
        splitTableType={splitTableType}
        onTakeInClick={handleTakeInClick}
        onBackToNormalTable={handleBackToNormalTable}
        searchQuery={searchQuery}
        filters={filters}
        onViewAllClick={handleViewAllClick}
      />

      <OrderDetailsModal
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        orderCode={selectedDetailsOrderCode}
      />
    </div>
  );
}